import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { useReportBundleStore } from '@/frontend/store/ReportBundleStore';
import { useSchemaStore } from '@/frontend/store/SchemaStore';
import type { CreateReportBundleRequest } from '@/types/reportBundleTypes';
import { LoadingSpinner } from '../LoadingSpinner';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';

/** Where the schema of a new report comes from: the example's, an empty one of its own, or one there is already. */
type SchemaSource = 'example' | 'empty' | 'existing';
const SOURCES: Array<{ source: SchemaSource; label: string; help: string }> = [
  { source: 'example', label: 'reportBundleSchemaExample', help: 'reportBundleSchemaExampleHelp' },
  { source: 'empty', label: 'reportBundleSchemaEmpty', help: 'reportBundleSchemaEmptyHelp' },
  { source: 'existing', label: 'reportBundleSchemaExisting', help: 'reportBundleSchemaExistingHelp' },
];

/** a choice card: the radio and its words, outlined when picked */
const CARD = 'flex cursor-pointer items-start gap-2.5 rounded-lg border-[1px] p-3 transition-colors has-data-checked:border-primary has-data-checked:bg-primary/5';

/**
 * A new report, in two questions: what it reports on — what someone enters on
 * a schema (a procedure run is not offered yet: procedures do not use coded
 * reports) — and which schema: a new one from the example, a new empty one,
 * or one there is already, followed at its draft or fixed at its latest
 * version. Created, it opens in the designer.
 */
export function CreateReportBundleModal({ onHide, schemaId: given }: { onHide: () => void; /** start from this schema, as a schema's own page does */ schemaId?: string }) {
  const createBundle = useReportBundleStore((s) => s.createBundle);
  const schemas = useSchemaStore((s) => s.schemas);
  const fetchSchemas = useSchemaStore((s) => s.fetchSchemas);
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState<SchemaSource>(given ? 'existing' : 'example');
  const [schemaId, setSchemaId] = useState<string | null>(given ?? null);
  const [find, setFind] = useState('');
  /** follow the existing schema's draft, or fix it at its latest version */
  const [pin, setPin] = useState<'draft' | 'latest'>('latest');
  const [saving, setSaving] = useState(false);

  // the list is fetched afresh when started from a schema: one created since the list was cached would be missing from it
  useEffect(() => {
    if (given) void fetchSchemas();
  }, [given, fetchSchemas]);
  useEffect(() => {
    if (source === 'existing' && schemas === null) void fetchSchemas();
  }, [source, schemas, fetchSchemas]);

  const shown = useMemo(() => (schemas ?? []).filter((s) => s.title.toLowerCase().includes(find.trim().toLowerCase())), [schemas, find]);
  const picked = schemas?.find((s) => s.id === schemaId) ?? null;
  // a schema never published can only be followed at its draft
  const pinsLatest = pin === 'latest' && !!picked?.latestVersion;

  const request = (): CreateReportBundleRequest | null => {
    const base = { title: title.trim(), description: description.trim() };
    if (source === 'example') return { ...base, starter: 'inputs' };
    if (source === 'empty') return { ...base, starter: 'inputs-blank' };
    if (!picked) return null;
    return { ...base, starter: 'inputs-blank', schema: pinsLatest ? { key: picked.key, version: picked.latestVersion?.number } : { key: picked.key } };
  };

  const handleCreate = async () => {
    const data = request();
    if (!data) return;
    setSaving(true);
    try {
      const bundle = await createBundle(data);
      if (bundle) {
        onHide();
        void navigate(`/report-studio/${bundle.id}`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onHide(); }}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-xl">
        <DialogHeader><DialogTitle>{I18n.get('newReport')}</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="new-bundle-title">{I18n.get('reportBundleTitle')}</Label>
          <Input id="new-bundle-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="new-bundle-description">{I18n.get('reportBundleDescription')}</Label>
          <Textarea id="new-bundle-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className="grid gap-2">
          <Label id="new-bundle-reads">{I18n.get('reportBundleReportsOn')}</Label>
          <RadioGroup aria-labelledby="new-bundle-reads" value="inputs" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className={CARD}>
              <RadioGroupItem value="inputs" className="mt-0.5" />
              <span className="grid gap-0.5">
                <span className="font-medium">{I18n.get('reportBundleReportsOnInputs')}</span>
                <span className="text-xs text-muted-foreground">{I18n.get('reportBundleReportsOnInputsHelp')}</span>
              </span>
            </label>
            <label className={cn(CARD, 'cursor-not-allowed border-dashed bg-muted/40')}>
              <RadioGroupItem value="run" disabled className="mt-0.5" />
              <span className="grid gap-0.5">
                <span className="font-medium text-muted-foreground">{I18n.get('reportBundleReportsOnRun')}</span>
                <span className="text-xs text-muted-foreground">{I18n.get('reportBundleReportsOnRunHelp')}</span>
              </span>
            </label>
          </RadioGroup>
        </div>

        <div className="grid gap-2">
          <Label id="new-bundle-schema">{I18n.get('reportBundleWhichSchema')}</Label>
          <RadioGroup aria-labelledby="new-bundle-schema" value={source} onValueChange={(value) => setSource(value as SchemaSource)} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {SOURCES.map((s) => (
              <label key={s.source} className={CARD}>
                <RadioGroupItem value={s.source} className="mt-0.5" />
                <span className="grid gap-0.5">
                  <span className="font-medium">{I18n.get(s.label)}</span>
                  <span className="text-xs text-muted-foreground">{I18n.get(s.help)}</span>
                </span>
              </label>
            ))}
          </RadioGroup>
        </div>

        {source === 'existing' && (
          <div className="grid gap-2">
            <Input aria-label={I18n.get('reportBundleSchemaFind')} placeholder={I18n.get('reportBundleSchemaFind')} value={find} onChange={(e) => setFind(e.target.value)} />
            {schemas === null ? (
              <div className="flex justify-center py-4" aria-label={I18n.get('loading')}><LoadingSpinner /></div>
            ) : shown.length === 0 ? (
              <p className="py-2 text-xs text-muted-foreground">{I18n.get('reportBundleSchemaNoneFound')}</p>
            ) : (
              <RadioGroup aria-label={I18n.get('reportBundleSchemaExisting')} value={schemaId ?? ''} onValueChange={(value) => setSchemaId(value as string)} className="max-h-56 gap-0 overflow-y-auto rounded-lg border-[1px]">
                {shown.map((s) => (
                  <label key={s.id} className="flex cursor-pointer items-center gap-3 border-b-[1px] px-3 py-2 last:border-b-0 has-data-checked:bg-primary/5">
                    <RadioGroupItem value={s.id} />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="font-medium [overflow-wrap:anywhere]">{s.title}</span>
                      <span className="text-xs text-muted-foreground">
                        {s.usedBy.length ? I18n.get('reportBundleSchemaReadBy', { reports: s.usedBy.map((u) => u.title).join(', ') }) : I18n.get('reportBundleSchemaReadByNone')}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">{s.latestVersion ? `v${s.latestVersion.number}` : I18n.get('reportBundleSchemaDraftOnly')}</span>
                  </label>
                ))}
              </RadioGroup>
            )}
            {picked && (
              <RadioGroup aria-label={I18n.get('reportBundleSchemaRead')} value={pinsLatest ? 'latest' : 'draft'} onValueChange={(value) => setPin(value as 'draft' | 'latest')} className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                {picked.latestVersion && (
                  <label className="flex cursor-pointer items-center gap-2"><RadioGroupItem value="latest" /> {I18n.get('reportBundleSchemaPin', { number: String(picked.latestVersion.number) })}</label>
                )}
                <label className="flex cursor-pointer items-center gap-2"><RadioGroupItem value="draft" /> {I18n.get('reportBundleSchemaFollow')}</label>
              </RadioGroup>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onHide} disabled={saving}>{I18n.get('cancel')}</Button>
          <Button onClick={() => void handleCreate()} disabled={saving || title.trim() === '' || request() === null}>
            {saving ? I18n.get('saving') : I18n.get('reportBundleCreate')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
